import JwtService from "@/services/jwt.service"
import { UsersService } from "@/services/api/users.api.service"
import { ClinicService } from "@/services/api/clinic.api.service"
import { EloquentService } from "@/services/api/eloquent.api.service"
import { CRMService } from "@/services/api/crm.api.service"
import { PatientService } from "@/services/api/patient.api.service"
import LangHelper from "@/services/lang.helper"
import BranchHelper, {saveBranch} from "@/services/branch.helper"
import i18n from '@/lang/i18n'
import moment from 'moment'
import axios from "axios"
import {countries} from "@/dictionaries/dictionary"

const state = {
    errors: {},
    user: null,
    clinic: null,
    branches: [],
    doctors: [],
    doctors_with_blocked: [],
    curators: [],
    superadmins: [],
    admins: [],
    assistants: [],
    chairs: [],
    branchChairs: [],
    tasks: [],
    configs: [],
    prices: [],
    units: {},
    rights: [],
    pay_methods: [],
    record_reasons: [],
    record_types: [],
    deposit_accounts: [],
    professions: [],
    record_tags: [],
    clientSources: [],
    CRMStages: [],
    patientCanbanStatuses: [],
    passwordRequirements: null,
    mustChangePassword: false,
    pendingCurrentPassword: null,
    pendingLegalDocuments: [],
    legalDocumentsEnabled: false,
    superadmin: JwtService.isSuperAdmin(),
    branch: BranchHelper.getBranch(),
    isLogged: !!JwtService.getToken(),
    paid_till: null,
    free_day: null,
    clinicTariff: null,
    urlAfterLogin: null,
    serverCountry: 'RU',
    useProxy: true,
    flutterApp: false,

    headerUpdateCounter: 0,

    headerCounters: {
        tasks: 0,
        birthdays: 0,
        waiting: 0,
        notifications: 0,
        unread: 0,
        excels: 0,
    }
}

const getters = {
    user(state) {
        return state.user
    },
    isLogged(state) {
        return state.isLogged
    },
    branchObject() {
        return state.branches.find(obj => obj.id === state.branch)
    },
    isPatient(state) {
        return state.user && Number(state.user.role_id) === 5
    },
    isDoctor(state) {
        return state.user && Number(state.user.role_id) === 4
    },
    isDoctorOrAssistant(state) {
        return state.user && [4,7].includes(Number(state.user.role_id))
    },
    getCurrencyName(state) {
        return state.clinic.currency ? state.clinic.currency.name : ''
    },
    hasFreeTariff() {
        if(!state.clinicTariff) return false
        return state.clinicTariff.tariff_id == 1
    },
    isPaid(state) {
        let today = moment().format('YYYY-MM-DD')
        if(state.free_day && state.free_day == today) return true
        if(state.paid_till && state.paid_till >= today) return true
        return false
    },
    usesBonuses(state) {
        return state.rights.includes('bonuses') && (state.clinic?.clinic_additional?.bonuses_enabled || false)
    },
    deductsAcquiringFromSalaries(state) {
        return state.clinic?.clinic_additional?.deduct_acquiring_from_salary || false
    },
    deductsMaterialsFromSalaries(state) {
        return state.rights.includes('materials') && (state.clinic?.clinic_additional?.deduct_materials_from_doctor_salary || false)
    },
    deductTechnicSumFromSalaries(state) {
        return state.clinic.percent_accrual === 'after_technic'
    },
    recordTimingIsOn(state) {
        return state.clinic?.clinic_additional?.record_timing_enabled || false
    },
    severalOpenedVisitsEnabled(state, getters) {
        return (state.clinic?.clinic_additional?.several_opened_visits || false) || !getters.recordTimingIsOn
    },
    ppoSettings(state) {
        return state.clinic?.ppo_settings ? JSON.parse(state.clinic.ppo_settings) : {}
    },
    getClinicAdditional: (state) => (key) => {
        return state.clinic?.clinic_additional?.[key] || false
    },
    getPaymentById: (state) => (payId) => {
        return state.pay_methods.find(obj => Number(obj.id) === Number(payId))
    },
    dynamicConfigValue: (state, getters) => (key, branchId = null, defaultToOtherBranch = false) => {
        let res = state.configs.find(config => config.title === key && (!branchId || Number(branchId) === Number(config.branch_id)))?.value || null
        if(!res && branchId && defaultToOtherBranch) {
            return getters.dynamicConfigValue(key)
        }
        return res
    },
    jetAPIEnabled(state, getters) {
        return getters.dynamicConfigValue('jetAPIRealInstanceId') && getters.dynamicConfigValue('jetAPIToken')
    },
    jetAPIMaxEnabled(state, getters) {
        return Number(getters.dynamicConfigValue('jetAPIMaxConnected')) === 1
    },
    greenAPIEnabled(state, getters) {
        return getters.dynamicConfigValue('greenAPIInstanceId') && getters.dynamicConfigValue('greenAPIToken')
    },
    greenAPIMaxEnabled(state, getters) {
        return !!(getters.dynamicConfigValue('greenAPIInstanceIdMax') && getters.dynamicConfigValue('greenAPITokenMax'))
    },
    greenAPIIframeUrl: (state, getters) => (phone = null, platform = null) => {
        if(!platform && getters.greenAPITelegramPanelEnabled) platform = 'telegram'
        if(!platform && getters.greenAPIMaxPanelEnabled) platform = 'max'
        if(!platform && getters.greenAPIPanelEnabled) platform = 'whatsapp'
        if(!platform) return null
        const prefixes = { 'max': 'Max', 'telegram': 'Telegram', 'whatsapp': '' }
        const prefix = prefixes[platform]
        const idInstance = getters.dynamicConfigValue(`greenAPIInstanceId${prefix}`)
        const apiTokenInstance = getters.dynamicConfigValue(`greenAPIToken${prefix}`)
        let apiUrl = getters.dynamicConfigValue(`greenAPIUrl${prefix}`)
        if(!idInstance || !apiTokenInstance || !apiUrl) {
            return null
        }
        if(apiUrl?.endsWith('/')) {
            apiUrl = apiUrl.slice(0, -1)
        }
        const mediaUrl = apiUrl?.replace('api.green-api.com', 'media.green-api.com') || ''
        let url = `https://chats.green-api.com?idInstance=${idInstance}&apiTokenInstance=${apiTokenInstance}&apiUrl=${apiUrl}&mediaUrl=${mediaUrl}`
        if(phone) {
            url += `&lng=ru`
            if(platform === 'whatsapp') url += `&chatId=${phone}@c.us`
            if(platform === 'telegram') url += `&chatId=${phone}`
            if(platform === 'max') url += `&chatId=${phone}`
        }
        return url
    },
    canSendViaWhatsApp(state, getters, rootState) {
        return getters.jetAPIEnabled || getters.greenAPIEnabled || rootState.crm.wazzupEnabled
    },
    canSendViaTelegram(state, getters, rootState) { // both are the same for now
        return getters.jetAPIEnabled || getters.greenAPIEnabled || rootState.crm.wazzupEnabled
    },
    onlineContractEnabled(state, getters) {
        return !!getters.dynamicConfigValue('online_contract_token')
    },
    telegramBotEnabled(state, getters) {
        return !!getters.dynamicConfigValue('telegramBotToken')
    },
    FDocEnabled(state, getters) {
        return !!getters.dynamicConfigValue('fdocCorpId')
    },
    trustMeEnabled(state, getters) {
        return !!getters.dynamicConfigValue('trustme_token')
    },
    esiaEnabled(state, getters) {
        return !!getters.dynamicConfigValue('esiaToken')
            && state.rights.includes('esia_sign')
            && getters.countryId === countries.RUSSIA
    },
    diagnocatEnabled(state, getters) {
        return state.rights.includes('diagnocat') && !!getters.dynamicConfigValue('diagnocatLogin')
    },
    maxEnabled(state, getters) {
        return getters.greenAPIMaxEnabled || getters.jetAPIMaxEnabled
    },
    jetAPIPanelEnabled(state, getters) {
        return getters.jetAPIEnabled
            && !getters.auraPanelEnabled
            && getters.dynamicConfigValue('jetAPIShowPanel')
            && (getters.dynamicConfigValue('jetAPIPanelUsers') || []).includes(state.user.id)
    },
    greenAPIPanelEnabled(state, getters) {
        return !getters.jetAPIPanelEnabled
            && !getters.auraPanelEnabled
            && getters.dynamicConfigValue('greenAPIShowPanel')
            && (getters.dynamicConfigValue('greenAPIPanelUsers') || []).includes(state.user.id)
    },
    greenAPITelegramPanelEnabled(state, getters) {
        return !getters.jetAPIPanelEnabled
            && !getters.auraPanelEnabled
            && getters.dynamicConfigValue('greenAPIShowPanelTelegram')
            && (getters.dynamicConfigValue('greenAPIPanelUsersTelegram') || []).includes(state.user.id)
    },
    greenAPIMaxPanelEnabled(state, getters) {
        return !getters.jetAPIPanelEnabled
            && !getters.auraPanelEnabled
            && getters.dynamicConfigValue('greenAPIShowPanelMax')
            && (getters.dynamicConfigValue('greenAPIPanelUsersMax') || []).includes(state.user.id)
    },
    auraPanelEnabled(state, getters) {
        return getters.dynamicConfigValue('auraEnabled')
    },
    getPPOAttribute: (state, getters) => (attribute) => {
        return getters.ppoSettings[attribute] || null
    },
    listLeadResponsible: (state) => (lead) => {
        if(lead.multiple_responsible && lead.multiple_responsible.length) {
            if(state.admins.length === lead.multiple_responsible.length) {
                if(state.admins.map(x => x.id).sort().join(',') === lead.multiple_responsible.map(x => x.id).sort().join(',')) {
                    return i18n.global.t('all_administrators')
                }
            }
            return lead.multiple_responsible.map(x => x.full_name).join(', ')
        }
        if(lead.responsible) {
            return lead.responsible.full_name
        }
        return ''
    },
    countryId(state) {
        return Number(state.clinic?.country_id) || 0
    },
    getStaffUsers(state) {
        return [
            ...state.doctors,
            ...state.admins,
            ...state.curators,
            ...state.assistants,
            ...state.superadmins,
        ].map(obj => ({ id: obj.id, name: obj.full_name }))
    },
    timeStart(state, getters) {
        return getters.branchObject?.time_start || state.clinic?.time_start
    },
    timeEnd(state, getters) {
        return getters.branchObject?.time_end || state.clinic?.time_end
    },
    isDentalClinic(state) {
        // console.log(state)
        // return false
        const isMedical = state.clinic?.business_type === 'medical'
        return !isMedical
    },
    visitRequiredFields(state) {
        const arr = []
        const additional = state.clinic?.clinic_additional || {}
        Object.keys(additional).forEach(key => {
            if(key.startsWith('close_record_requires_') && additional[key]) {
                arr.push(key.replace('close_record_requires_', ''))
            }
        });
        return arr
    }
}

const mutations = {
    setError(state, error) {
        state.errors = error
    },
    setBranch(state, branch) {
        state.branch = branch
        BranchHelper.saveBranch(branch)
    },
    setBranchWithoutLocalStorage(state, branch) {
        state.branch = branch
    },
    setUser(state, user) {
        state.user = user
    },
    setClientSources(state, sources) {
        state.clientSources = sources
    },
    setUseProxy(state, value) {
        state.useProxy = value
    },
    setCRMStages(state, value) {
        state.CRMStages = value
    },
    setPatientCanbanStatuses(state, value) {
        state.patientCanbanStatuses = value
    },
    setAuth(state, data) {
        state.isLogged = true
        state.mustChangePassword = !!data.must_change_password
        state.user = data.user
        state.errors = {}
        if(data.token) {
            JwtService.saveToken(data.token, data.superadmin ? data.superadmin : false)
        }
        if(!data.use_proxy) {
            axios.defaults.baseURL = process.env.VUE_APP_API_URL
            state.useProxy = false
        }
        if(data.clinic) {
            state.clinic = data.clinic
            if(state.clinic.lang_code) {
                i18n.global.locale = state.clinic.lang_code
                LangHelper.saveLanguage(state.clinic.lang_code)
                moment.locale(state.clinic.lang_code)
            }
            if(state.clinic.currency) {
                window.localStorage.setItem('currency', state.clinic.currency.name)
            }
        }
        if(!state.branch && (data.user.branches || []).length) {
            let branch = data.user.branches[0].id
            state.branch = branch
            BranchHelper.saveBranch(branch)
        }
        if(data.branches) {
            state.branches = data.branches
            if(!state.branch && data.branches.length > 0) {
                let branch = data.branches[0].id
                state.branch = branch
                BranchHelper.saveBranch(branch)
            }
        }
        if(data.doctors) state.doctors = data.doctors
        if(data.doctors_with_blocked) state.doctors_with_blocked = data.doctors_with_blocked
        if(data.curators) state.curators = data.curators
        if(data.server) state.serverCountry = data.server
        if(data.admins) {
            state.admins = data.admins
        }
        if(data.superadmins) {
            state.superadmins = data.superadmins
        }
        if(data.assistants) {
            state.assistants = data.assistants
        }
        if(data.prices) state.prices = data.prices
        if(data.chairs) {
            state.chairs = data.chairs
            state.branchChairs = data.chairs.filter(x => Number(x.branch_id) === Number(state.branch))
        }
        if(data.units) {
            state.units = data.units
        }
        state.configs = data.configs || []
        if(data.pay_methods) {
            state.pay_methods = data.pay_methods
        }
        if(data.record_reasons) {
            state.record_reasons = data.record_reasons
        }
        if(data.record_types) {
            state.record_types = data.record_types
        }
        if(data.rights) {
            state.rights = data.rights
        }
        if(data.professions) {
            state.professions = data.professions
        }
        if(data.superadmin) {
            state.superadmin = true
        }
        if(data.record_tags) {
            state.record_tags = data.record_tags
        }
        if(data.tariff) {
            state.paid_till = data.tariff.paid_till ?? null
            state.free_day = data.tariff.free_day ?? null
            state.clinicTariff = data.tariff
        }
        if(data.password_requirements) {
            state.passwordRequirements = data.password_requirements
        }
        state.pendingLegalDocuments = data.pending_legal_documents || []
        state.legalDocumentsEnabled = !!data.legal_documents_enabled
    },
    // used for the reduced login/initial payload returned when must_change_password
    // is true - a full setAuth isn't possible since clinic/rights/etc aren't sent
    setMustChangePassword(state, data) {
        state.isLogged = true
        state.mustChangePassword = true
        state.user = data.user
        if(data.user?.branch_id) {
            saveBranch(state.user.branch_id)
        }
        state.errors = {}
        if(data.password_requirements) {
            state.passwordRequirements = data.password_requirements
        }
        if(data.token) {
            JwtService.saveToken(data.token)
        }
    },
    setPendingCurrentPassword(state, password) {
        state.pendingCurrentPassword = password
    },
    setClinic(state, data) {
        state.clinic = data.clinic
    },
    setBranches(state, branches) {
        state.branches = branches
    },
    setAuthDoctors(state, doctors) {
        state.doctors = doctors
    },
    setTasks(state, tasks) {
        state.tasks = tasks
    },
    setHeaderCounter(state, { counter, value } ) {
        state.headerCounters[counter] = value
    },
    incrementHeaderUpdateCounter(state) {
        state.headerUpdateCounter++
    },
    setFreeDay(state, value) {
        state.free_day = value
    },
    setPaidTill(state, value) {
        state.paid_till = value
    },
    setAdmins(state, value) {
        state.admins = value
    },
    setUrlAfterLogin(state, value) {
        state.urlAfterLogin = value
    },
    setDepositAccounts(state, value) {
        state.deposit_accounts = value
    },
    setConfigs(state, value) {
        state.configs = value || []
    },
    setFlutterApp(state, value) {
        state.flutterApp = value
    },
    setPasswordRequirements(state, value) {
        state.passwordRequirements = value
    }
}

const actions = {
    login(context, params) {
        return new Promise(resolve => {
            UsersService.login(params).then(data => {
                const d = data.data
                if(d.requires_2fa) {
                    resolve({ requires_2fa: true })
                    return
                }
                if(d.must_change_password) {
                    context.commit('setMustChangePassword', d)
                    context.commit('setPendingCurrentPassword', params.password)
                    resolve({ must_change_password: true })
                    return
                }
                applyAuthData(context, d)
                resolve(d)
            }).catch(err => {
                context.commit('setError', err.response ? err.response.data.errors : {})
            })
        })
    },
    acceptLegalDocument(context, documentId) {
        return new Promise((resolve, reject) => {
            UsersService.acceptLegalDocument(documentId).then(data => {
                const d = data.data
                applyAuthData(context, d)
                resolve(d)
            }).catch(err => {
                context.commit('setError', err.response ? err.response.data.errors : {})
                reject(err.response ? err.response.data.errors : {})
            })
        })
    },
    changePassword(context, params) {
        return new Promise((resolve, reject) => {
            UsersService.changePassword(params).then(data => {
                const d = data.data
                applyAuthData(context, d)
                resolve(d)
            }).catch(err => {
                context.commit('setError', err.response ? err.response.data.errors : {})
                reject(err.response ? err.response.data.errors : {})
            })
        })
    },
    getPasswordRequirements(context) {
        return new Promise((resolve, reject) => {
            UsersService.getPasswordRequirements().then(data => {
                const requirements = data.data.password_requirements
                context.commit('setPasswordRequirements', requirements)
                resolve(requirements)
            }).catch(err => {
                reject(err.response ? err.response.data.errors : {})
            })
        })
    },
    forgotPassword(context, username) {
        return new Promise((resolve, reject) => {
            UsersService.forgotPassword(username).then(() => {
                resolve()
            }).catch(err => {
                context.commit('setError', err.response ? err.response.data.errors : {})
                reject(err.response ? err.response.data.errors : {})
            })
        })
    },
    sendStaffPasswordResetLink(context, { id, superadminClinicId = null }) {
        return new Promise((resolve, reject) => {
            UsersService.sendStaffPasswordResetLink(id, superadminClinicId).then(() => {
                resolve()
            }).catch(err => {
                context.commit('setError', err.response ? err.response.data.errors : {})
                reject(err.response ? err.response.data.errors : {})
            })
        })
    },
    resetPassword(context, params) {
        return new Promise((resolve, reject) => {
            UsersService.resetPassword(params).then(data => {
                const d = data.data
                applyAuthData(context, d)
                resolve(d)
            }).catch(err => {
                context.commit('setError', err.response ? err.response.data.errors : {})
                reject(err.response ? err.response.data.errors : {})
            })
        })
    },
    async logout(context) {
        if(JwtService.getToken()) {
            try {
                await UsersService.invalidateToken()
            } catch(e) {
                console.log(e)
            }
        }
        state.isLogged = false
        state.superadmin = false
        state.mustChangePassword = false
        state.pendingCurrentPassword = null
        state.pendingLegalDocuments = []
        state.user = null
        state.clinic = null
        state.branch = null
        state.paid_till = null
        state.free_day = null
        state.doctors = []
        state.branches = []
        state.chairs = []
        state.tasks = []
        state.errors = {}
        window.localStorage.removeItem('currency')
        BranchHelper.saveBranch(null)
        JwtService.destroyToken()
        context.dispatch('nullifyOpenedVisit')
        context.dispatch('nullifySelectedDoctors')
        context.dispatch('dataTableLogout')
        window.localStorage.setItem('logout', Date.now().toString()) // it is to notify other tabs about logout
    },
    init(context) {
        return new Promise((resolve, reject) => {
            UsersService.init(state.superadmin).then(data => {
                const d = data.data
                if(d.must_change_password) {
                    context.commit('setMustChangePassword', d)
                    resolve(d)
                    return
                }
                applyAuthData(context, d)
                context.dispatch('checkSelectedStaff', null, { root: true })
                resolve(d)
            }).catch(err => {
                context.commit('setError', err.response ? err.response.data.errors : {})
                reject(err.response ? err.response.data.errors : {})
            })
        })
    },
    saveFCMToken(context, user) {
        if(!user) return
        if(state.flutterApp) {
            window.flutter_inappwebview.callHandler('userLoggedIn', user.id).then(data => {
                if(data && data.token && data.platform) {
                    const params = {
                        token: data.token,
                        type: data.platform,
                        user_id: user.id
                    }
                    let existingToken = user.fcm_tokens.find(x => x.token === data.token)
                    if(!existingToken) UsersService.fcmToken(params)
                }
            }).catch(e => {
                console.log(e)
            })
        }
    },
    saveData(context, params) {
        return new Promise((resolve, reject) => {
            ClinicService.saveData(params).then(data => {
                let d = data?.data
                context.commit('setClinic', d)
                saveCalendarSettings(context, d)
                resolve(d)
            }).catch(err => {
                context.commit('setError', err.response.data.errors)
                reject(err.response.data.errors)
            })
        })
    },
    getTasks(context, params = { loading: false, page: 1 }) {
        const page = params.page || 1
        return new Promise((resolve, reject) => {
            EloquentService.dataTable('task', page, params, params.loading).then(data => {
                let d = data.data.result
                const tasks = page === 1 ? d.data : context.state.tasks.concat(d.data)
                context.commit('setTasks', tasks)
                resolve(d)
            }).catch(err => {
                reject(err.response.data.errors)
            })
        })
    },
    async getClientSources(context) {
        const sources = await PatientService.getClientSources()
        context.commit('setClientSources', sources.data)
    },
    async getCRMStages(context) {
        const crmStages = await CRMService.getStatuses()
        context.commit('setCRMStages', crmStages.data)
    },
    async getPatientCanbanStatuses(context, mode = 'manual') {
        const crmStages = await CRMService.getPatientCanbanStatuses(mode)
        context.commit('setPatientCanbanStatuses', crmStages.data)
    },
    async getAdmins(context) {
        if(!context.state.admins.length) {
            const data = await ClinicService.getAdmins()
            context.commit('setAdmins', data.data)
        }
    },
    async getDepositAccounts(context) {
        if(!context.state.deposit_accounts.length) {
            const depositAccountsData = await EloquentService.dataTable('deposit_account')
            const depositAccounts = depositAccountsData?.data?.result?.data || []
            context.commit('setDepositAccounts', depositAccounts)
        }
    },
}

function applyAuthData(context, d) {
    context.commit('setAuth', d)
    context.commit('setMyOpenedVisits', d.my_opened_visits)
    saveCalendarSettings(context, d)
    context.dispatch('saveFCMToken', d.user)
}

function saveCalendarSettings(context, data = null) {
    if(data?.clinic) {
        const clinic = data.clinic
        const branch = (data?.branches || []).find(b => Number(b.id) === Number(context.state.branch))
        const timeStart = branch?.time_start || clinic?.time_start
        const timeEnd = branch?.time_end || clinic?.time_end
        context.commit('setTimeStep', parseInt(clinic.calendar_step))
        context.commit('setTimeFromHour', parseInt(timeStart))
        let timeToHour = parseInt(timeEnd) + 1
        if(timeEnd.substr(timeEnd.length - 5) === '00:00') timeToHour--
        context.commit('setTimeToHour', timeToHour)
    }
}

export default {
    state,
    actions,
    mutations,
    getters
}
